import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import type { Legend } from '../../../ui/legend/Legend';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'markerLine',
  name_en: 'Marker Line',
  // name_zh: '大事指标折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: (amount: number) => amount > 1,
    x: (amount: number) => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y2: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    subTitle: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE,
      optional: true
    },
    desc: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE,
      optional: true
    },
    link: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE,
      optional: true
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    y2: string;
    subTitle?: string;
    desc?: string;
    link?: string;
  };
}

/**
 * 将输入字符串根据字符'\n'分割成片段，超过maxCharNum的片段进行分割，然后拼接成一个字符串。
 *
 * @param inputStr 输入字符串。
 * @param maxCharNum 最大允许字数。
 * @returns 处理完成的字符串。
 */
// 根据'\n'分割输入字符串
function processString(inputStr: string, maxCharNum: number, maxLineNum: number): string {
  const segments: string[] = inputStr.split('\n');

  // 处理每个片段
  const processedSegments: string[] = [];
  segments.forEach(segment => {
    // 如果片段字数超过maxCharNum，则进行分割
    if (segment.length > maxCharNum) {
      // 计算需要分割成多少个小片段
      const numSubSegments: number = Math.ceil(segment.length / maxCharNum);
      // 分割片段
      for (let i = 0; i < numSubSegments; i++) {
        const subSegment: string = segment.substring(i * maxCharNum, (i + 1) * maxCharNum);
        processedSegments.push(subSegment);
      }
    } else {
      // 如果不超过，则直接添加
      processedSegments.push(segment);
    }
  });

  // 如果分割后行数大于maxLineNum
  if (processedSegments.length > maxLineNum) {
    // 忽略行数大于maxLineNum的片段
    processedSegments.splice(maxLineNum);
    // 将第maxLineNum的字符串末尾替换为'...'
    processedSegments[maxLineNum - 1] = `${processedSegments[maxLineNum - 1].substring(
      0,
      processedSegments[maxLineNum - 1].length - 3
    )}...`;
  }

  // 将处理完成的片段通过'\n'拼接成一个字符串
  const resultStr: string = processedSegments.join('\n');

  return resultStr;
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function
  render(api, spec, $dom) {
    const { data: dataArray, util, hook, token } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, y2, desc, subTitle, link }
    } = spec;
    const markerLineToken = api.token.dvMarkerLine;
    const data = dataArray[dataIndex];
    const originData = data.values.slice();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const hasDesc = !!desc;
    const hasSubTitle = !!subTitle;
    const hasLink = !!link;

    // * -------------------------- data
    const dataMetaInfo = api.hook.parseDataMetaInfo(data);
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' },
      { type: 'deDuplication', field: x }
    ]) as (typeof data)['values'];
    const { xAxisData, yAxisData } = util.dataEncoding.encoding(transformData, {
      xAxisData: { field: x },
      yAxisData: { field: y, transforms: [{ type: 'toNumeric', field: y }] }
    });

    const { y2Data } = util.dataEncoding.encoding(data.values.slice(), {
      y2Data: {
        field: y2,
        transforms: [{ type: 'filter', cb: value => value != null }, { type: 'deDuplication' }]
      }
    });

    // 散点series对应的y轴范围的最大值
    let y2AxisDataMax = 0;
    // 当前单个x轴刻度下 存在散点的最大数量
    let currPriority = 0;
    const xAxisToTopScatter = new Map<number, { seriesName: string | number; dataName: string }>();
    // 每个标记单行最多字数
    const maxCharNum = 14;
    // 每个标记描述的最大行数
    const maxLineNum = 3;
    let lastX: (string | number) | null = null;
    // 最后一个显示的标记对应的legend名称
    let lastMarkerType: string;
    const priorityAttr = '__priority__';
    const scatterUID = 'suid';
    const markerData: {
      value: (string | number)[];
      __name: string | number;
      [key: string]: string | number | (string | number)[];
    }[] = [];
    originData.forEach((item, originIndex) => {
      if (!item[y2]) {
        return;
      }

      if (lastX !== item[x]) {
        // 当遍历到下一个x轴刻度时，确定上一刻度的最上方散点，用于实现axisPointer的高亮交互
        if (lastX !== null && markerData.length) {
          const { currPriority: priority } = markerData[markerData.length - 1];
          const dataName = (markerData[markerData.length - 1].name = `${lastX}${priority}`);
          xAxisToTopScatter.set(
            xAxisData.findIndex(i => i === lastX),
            { dataName, seriesName: markerData[markerData.length - 1].__name }
          );
        }
        currPriority = 0;
        lastX = item[x];
        // lastMarkerType = item[y2] as string;
      } else {
        currPriority += 1;
      }
      markerData.push({
        value: [item[x], item[y], item[y2]],
        name: `${item[x]}${currPriority}`,
        __name: item[y2],
        currPriority,
        title: `${item[y2]}${hasSubTitle && item[subTitle] ? `-${item[subTitle]}` : ''}`,
        desc: hasDesc ? processString(item[desc] as string, maxCharNum, maxLineNum) : '',
        link: hasLink ? item[link] : ''
      });

      if (lastX !== null && markerData.length && originIndex === originData.length - 1) {
        const { currPriority: priority } = markerData[markerData.length - 1];
        const dataName = (markerData[markerData.length - 1].name = `${lastX}${priority}`);
        xAxisToTopScatter.set(
          xAxisData.findIndex(i => i === lastX),
          { dataName, seriesName: markerData[markerData.length - 1].__name }
        );
      }
      item[priorityAttr] = currPriority;
      item[scatterUID] = `${item[x]}${currPriority}`;
      y2AxisDataMax = Math.max(y2AxisDataMax, currPriority);
    });
    console.log('markerData', markerData, xAxisToTopScatter, xAxisData);

    // * -------------------------- legend

    const y2LegendData = y2Data.map(item => ({ name: item, y2Legend: true }));
    const legendData = [y, ...y2LegendData].map((item, index) => ({
      name: util.data.isObject(item) ? (item as (typeof y2LegendData)[number]).name : item,
      color: presetOption?.color?.[index % (presetOption?.color?.length || 0)] || 'blue',
      type: util.const.SERIES_2_LEGEND_TYPE_MAP[
        (item as (typeof y2LegendData)[number]).y2Legend ? 'scatter' : 'line'
      ],
      // selected: item?.name === lastMarkerType || typeof item?.name === 'undefined',
      selected: true,
      disableSelect: index <= 0
    }));

    // * -------------------------- chart
    const chartRect = $dom.getBoundingClientRect();
    const dataZoomHeight = 16;
    // 散点的大小
    const scatterSize = 10;
    // 两个散点的上下间距
    const scatterSpace = 2;
    // 标记距离顶部的边距
    const markerTopPadding = 5;
    const gridMargin = 12 + 10 * 2;
    const heightOfSpaceOutsideGrid = dataZoomHeight + 8 + gridMargin + 14 + 8;
    const gridHeight = chartRect.height - heightOfSpaceOutsideGrid;
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption,
      height: gridHeight
    };
    const dvSplitLineNumber = hook.getSplitLineNumber(gridHeight - grid.top - grid.bottom);
    const y2AxisData = Array.from(
      { length: Math.floor(gridHeight / (scatterSize + scatterSpace)) },
      (_, index) => index
    );

    const [dataZoomStartIndex, dataZoomEndIndex] = [0, xAxisData.length - 1];
    const y2Series = y2Data
      .map((y2Item, index) => {
        const color =
          presetOption?.color?.[(index + 1) % (presetOption?.color?.length || 0)] || 'blue';
        const y2Datum = originData
          .filter(item => item[y2] === y2Item)
          .map(item => ({ value: [item[x], item[priorityAttr]], name: item[scatterUID] }));
        const y2MarkerData = markerData
          .filter(item => item.__name === y2Item)
          .map(item => {
            return {
              ...item,
              itemStyle: {
                textStyle: {
                  style: {
                    text: `{logo|}{margin10|}{title|${item.title}}${
                      item.desc ? `\n{marginTop8|11}\n{desc|${item.desc}}` : ''
                    }`,
                    rich: {
                      logo: {
                        width: 10,
                        height: 10,
                        borderRadius: 10,
                        backgroundColor: color,
                        lineHeight: 16
                      },
                      margin10: {
                        width: 6,
                        backgroundColor: 'transparent'
                      },
                      marginTop8: {
                        fontSize: 4,
                        fill: 'transparent'
                      },
                      title: {
                        fontSize: 12,
                        fontWeight: 550,
                        lineHeight: 16,
                        fill: markerLineToken?.titleColor ?? '#000'
                      },
                      desc: {
                        fontSize: 12,
                        lineHeight: 16,
                        fill: markerLineToken?.descColor ?? 'rgba(84, 94, 113, 1)'
                      }
                    }
                  }
                }
              }
            };
          });
        return [
          {
            type: 'scatter',
            name: y2Item,
            yAxisIndex: 1,
            symbolSize: scatterSize,
            itemStyle: {
              color,
              opacity: 1
            },
            emphasis: {
              itemStyle: {
                borderColor: markerLineToken?.scatterBorderColor ?? '#000',
                borderWidth: 1
              }
            },
            data: y2Datum
          },
          {
            type: 'dvMarker',
            dvMarkType: 'flag',
            name: y2Item,
            data: y2MarkerData,
            // flagLayoutHandler: (list, minBound, maxBound, handlers, flag) => {},
            dvItemStyle: {
              lineStyle: {
                style: {
                  lineHeight: 40,
                  lineWidth: 1.5,
                  stroke: util.style.colorOnBackground(
                    color,
                    0.5,
                    markerLineToken?.markerBackground
                  ),
                  lineDash: [5, 2]
                },
                emphasis: {
                  style: {
                    stroke: color
                  }
                },
                z2: 1
              },
              rectStyle: {
                shape: { width: 60, r: 4 },
                style: {
                  fill: util.style.colorOnBackground(color, 0.2, markerLineToken?.markerBackground),
                  opacity: 0.6,
                  lineWidth: 1,
                  stroke: util.style.colorOnBackground(
                    color,
                    0.8,
                    markerLineToken?.markerBackground
                  )
                },
                emphasis: {
                  style: {
                    fill: util.style.colorOnBackground(
                      color,
                      0.4,
                      markerLineToken?.markerBackground
                    ),
                    stroke: color
                  }
                }
              },
              textStyle: {
                style: {
                  padding: [4, 8, 8, 8]
                }
              },
              circleStyle: {
                shape: {
                  r: 4
                },
                style: {
                  fill: markerLineToken?.markerCircleColor ?? '#fff',
                  lineWidth: 1,
                  stroke: presetOption.color?.[0] ?? 'blue'
                },
                emphasis: {
                  style: {
                    lineWidth: 2
                  }
                },
                z2: 2
              }
            },
            z: 999
          }
        ];
      })
      .flat();

    const option = {
      legend: {
        // selectedMode: 'single',
        data: legendData
      },
      grid,
      dataZoom: [
        {
          type: 'slider',
          show: true,
          bottom: 2,
          height: dataZoomHeight,
          realtime: true,
          startValue: dataZoomStartIndex,
          endValue: dataZoomEndIndex,
          xAxisIndex: [0, 1],
          moveHandleIcon: 'path://',
          moveHandleSize: -32,
          moveHandleStyle: {
            opacity: 0
          },
          dvMoveHandleOffset: -16,
          borderRadius: 1,
          handleSize: '100%',
          textStyle: {
            color: token?.datazoomLabelColor || '#7C7D86'
          },
          dataBackground: {
            areaStyle: {
              opacity: 0
            },
            lineStyle: {
              opacity: 0
            }
          },
          selectedDataBackground: {
            areaStyle: {
              opacity: 0
            },
            lineStyle: {
              opacity: 0
            }
          }
        }
      ],
      xAxis: [
        {
          show: true,
          type: 'category',
          boundaryGap: false,
          axisLine: { onZero: true },
          axisPointer: {
            show: true,
            triggerEmphasis: false,
            label: {
              backgroundColor: '#3366ff'
            },
            z: 2
          },
          data: xAxisData
        }
      ],
      yAxis: [
        {
          show: true,
          dvSplitLineNumber,
          type: 'value',
          scale: true,
          triggerEmphasis: false,
          axisLabel: {
            hideOverlap: true,
            formatter(value: number) {
              const result = hook.formatDataNumberValue(value);
              return result.value + result.unit;
            }
          },
          axisPointer: {
            show: true,
            triggerEmphasis: false,
            label: {
              backgroundColor: '#3366ff'
            },
            z: 2
          }
        },
        {
          show: false,
          type: 'category',
          data: y2AxisData,
          dvPadding: [0, 6],
          scale: true
        }
      ],
      tooltip: {
        trigger: 'item',
        show: false
      },
      series: [
        {
          name: y,
          type: 'line',
          showSymbol: false,
          areaStyle: {
            color: 'transparent'
          },
          emphasis: {
            itemStyle: {
              color: markerLineToken?.markerCircleColor ?? '#fff'
            }
          },
          stateAnimation: {
            duration: 0
          },
          data: yAxisData,
          z: 2
        },
        ...y2Series
      ]
    };
    return renderApi => {
      const legendIns = renderApi.renderLegendProxy(option.legend.data) as Legend;
      const renderParams = { option: renderApi.mergeChartOptionProxy(option) };
      const chartIns = renderApi.renderChartProxy(renderParams);

      // 点击标记图元: 如果标记带有link，则跳转空白标签页到link
      chartIns.on('dv:afterinit', () => {
        const ecIns = chartIns.getECharts();
        ecIns.on('click', e => {
          if (e?.data?.link) {
            window.open(e.data?.link, '_blank');
          }
        });
      });

      chartIns.on('dv:afterinit', () => {
        const ecIns = chartIns.getECharts();
        let lastDataIndex: number | null = null;
        const toggleTo = (
          event: 'highlight' | 'downplay',
          seriesIndex: number | string | null,
          dataIdx: number | string | null
        ) => {
          if (dataIdx == null || seriesIndex == null) {
            return;
          }
          if (typeof seriesIndex === 'string') {
            ecIns.dispatchAction({
              type: event,
              seriesName: seriesIndex,
              name: dataIdx
            });
          } else {
            ecIns.dispatchAction({
              type: event,
              seriesIndex,
              dataIndex: dataIdx
            });
          }
        };
        const highlightStorage = {
          lastHighlight: {
            topScatterSeriesName: null,
            xAxisDataName: null
          }
        };
        const handler = {
          set(
            target: typeof highlightStorage,
            property: keyof typeof highlightStorage,
            value: {
              topScatterSeriesName: string | null;
              xAxisDataName: string | null;
            }
          ) {
            const oldValue = target[property];
            if (
              !(
                value.topScatterSeriesName === oldValue.topScatterSeriesName &&
                value.xAxisDataName === oldValue.xAxisDataName
              )
            ) {
              // downplay旧状态
              toggleTo('downplay', oldValue?.topScatterSeriesName, oldValue?.xAxisDataName);
              // highlight新状态
              toggleTo('highlight', value?.topScatterSeriesName, value?.xAxisDataName);
            }

            target[property] = value;
            return true;
          }
        };

        const highlightProxy = new Proxy(highlightStorage, handler);

        ecIns.on('updateaxispointer', e => {
          const dataIdx = e.dataIndex as number;
          const topScatterSeriesName = xAxisToTopScatter.get(dataIdx)?.seriesName;
          const xAxisDataName = xAxisToTopScatter.get(dataIdx)?.dataName;

          if (dataIdx != null) {
            if (dataIdx !== lastDataIndex) {
              toggleTo('highlight', 0, dataIdx);
              toggleTo('downplay', 0, lastDataIndex);

              lastDataIndex = dataIdx;
              highlightProxy.lastHighlight = { topScatterSeriesName, xAxisDataName };
            }
          } else {
            toggleTo('downplay', 0, lastDataIndex);
            lastDataIndex = null;
            highlightProxy.lastHighlight = { topScatterSeriesName: null, xAxisDataName: null };
          }
        });

        ecIns.on('mousemove', 'series.scatter', e => {
          highlightProxy.lastHighlight = {
            topScatterSeriesName: e.seriesName,
            xAxisDataName: e.data?.name
          };
        });
      });

      // 图例事件处理：点击单个图例按钮时
      // 1. 如果其他图例状态为未选中，则先选中被点击的图例，然后选中其他图例
      // 2. 如果其他图例状态为选中，则先选中被点击的图例，然后取消选中其他图例
      legendIns.on('legendClick', e => {
        const ecIns = chartIns.getECharts();
        const global = legendIns.__config;
        const clickedStatus = e.data.selected;
        global.data.forEach(item => {
          if (!item.disableSelect) {
            if (item.name !== e.data.name) {
              const status = clickedStatus ? false : !item.selected;
              ecIns.dispatchAction({
                type: status ? 'legendSelect' : 'legendUnSelect',
                name: item.name
              });
              item.selected = status;
              legendIns.__updateLegendDom(item, item.dom);
            } else {
              ecIns.dispatchAction({
                type: 'legendSelect',
                name: e.data.name
              });
              item.selected = true;
            }
          }
        });
      });
    };
  }
});
